import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, Phone, MapPin } from 'lucide-react';
import { restaurantInfo, logoUrl } from '../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#sobre', label: 'Sobre' },
    { href: '#cardapio', label: 'Cardápio' },
    { href: '#delivery', label: 'Delivery' },
    { href: '#galeria', label: 'Galeria' },
    { href: '#localizacao', label: 'Localização' },
    { href: '#horarios', label: 'Horários' },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-neutral-950/95 backdrop-blur-md shadow-lg py-2'
          : 'bg-transparent py-4'
      }`}
      role="banner"
    >
      {/* Top bar */}
      <div className={`hidden md:block border-b border-white/10 transition-all duration-300 ${isScrolled ? 'h-0 overflow-hidden' : 'pb-2 mb-3'}`}>
        <div className="container mx-auto px-6 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6 text-white/70">
            <a href={`tel:${restaurantInfo.phone}`} className="flex items-center gap-2 hover:text-amber-400 transition-colors">
              <Phone size={14} aria-hidden="true" />
              <span>{restaurantInfo.phone}</span>
            </a>
            <span className="flex items-center gap-2">
              <MapPin size={14} aria-hidden="true" />
              <span>{restaurantInfo.address.neighborhood}, {restaurantInfo.address.city} - {restaurantInfo.address.state}</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href={restaurantInfo.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-amber-400 transition-colors"
              aria-label="Instagram do Japamix"
            >
              Instagram
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="container mx-auto px-6" aria-label="Navegação principal">
        <div className="flex items-center justify-between">
          {/* Logo - Larger */}
          <a href="#" className="flex items-center gap-3" aria-label="Japamix - Página inicial">
            <img 
              src={logoUrl} 
              alt="Logo Japamix restaurante japonês Anápolis" 
              className={`w-auto object-contain transition-all duration-300 ${
                isScrolled ? 'h-14 md:h-16' : 'h-16 md:h-20'
              }`}
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-white/90 hover:text-amber-400 transition-colors font-medium text-sm uppercase tracking-wider"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <Button
              className="bg-amber-700 hover:bg-amber-800 text-white font-semibold px-6 py-2.5 btn-hover-lift"
              onClick={() => window.open(`https://wa.me/${restaurantInfo.whatsapp}`, '_blank')}
              aria-label="Pedir agora via WhatsApp"
            >
              Pedir Agora
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-neutral-950/98 backdrop-blur-md transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-screen py-6' : 'max-h-0'
        }`}
        role="menu"
      >
        <nav className="container mx-auto px-6 flex flex-col gap-4" aria-label="Menu mobile">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-white/90 hover:text-amber-400 transition-colors font-medium py-2 border-b border-white/10 uppercase tracking-wider text-sm"
              role="menuitem"
            >
              {link.label}
            </a>
          ))}
          <Button
            className="bg-amber-700 hover:bg-amber-800 text-white font-semibold mt-4 w-full py-3"
            onClick={() => window.open(`https://wa.me/${restaurantInfo.whatsapp}`, '_blank')}
          >
            Pedir Agora
          </Button>
        </nav>
      </div>
    </header>
  );
};

export default Header;
